import { useState } from 'react';
import { api } from '../api/client';
import type { InspectResult, LinkResult } from '../api/types';
import { Player } from '../components/Player';
import { useApp } from '../stores/app';

function CopyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="field">
      <label>{label}</label>
      <div className="copybox">
        <input readOnly value={value} onFocus={(e) => e.currentTarget.select()} />
        <button className="shrink" onClick={() => void navigator.clipboard.writeText(value)}>
          Копировать
        </button>
      </div>
    </div>
  );
}

function LinkGenerator() {
  const { tokens, user } = useApp();
  const [form, setForm] = useState({ tokenId: '', src: '', profile: 'mobile', cam: '', obj: '' });
  const [result, setResult] = useState<LinkResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setPreview(false);
    try {
      setResult(await api.generateLink(form));
    } catch (err) {
      setResult(null);
      setError((err as Error).message);
    }
  };

  const useTestSource = async () => {
    if (!form.tokenId) {
      setError('Сначала выберите токен');
      return;
    }
    setError(null);
    setPreview(false);
    try {
      const res = await api.testSource(form.tokenId);
      setForm({ ...form, src: res.src });
      setResult(res);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="panel">
        <h2>Генератор ссылок</h2>
        <p className="muted">Доступен только администратору: ссылку можно выпустить на любую камеру.</p>
      </div>
    );
  }

  return (
    <div className="panel">
      <h2>Генератор ссылок</h2>
      <p className="muted" style={{ fontSize: 12 }}>
        Выдаёт ровно такой же URL, какой формирует Мажордом. Каждая генерация пишется в аудит.
      </p>

      <form onSubmit={submit}>
        <div className="row">
          <div className="field">
            <label>Токен</label>
            <select required value={form.tokenId} onChange={(e) => setForm({ ...form, tokenId: e.target.value })}>
              <option value="">— выберите —</option>
              {tokens.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.id})
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Профиль</label>
            <select value={form.profile} onChange={(e) => setForm({ ...form, profile: e.target.value })}>
              <option value="mobile">mobile — substream</option>
              <option value="hd">hd — основной поток</option>
            </select>
          </div>
        </div>

        <div className="field">
          <label>URL источника</label>
          <input
            required
            className="mono"
            placeholder="rtsp://admin:пароль@91.0.2.10:554/cam/realmonitor?channel=1&subtype=1"
            value={form.src}
            onChange={(e) => setForm({ ...form, src: e.target.value })}
          />
          <div className="hint">Как есть, с учётными данными регистратора — они уедут в конверт и наружу не попадут.</div>
        </div>

        <div className="row">
          <div className="field">
            <label>Камера (cam)</label>
            <input value={form.cam} onChange={(e) => setForm({ ...form, cam: e.target.value })} />
          </div>
          <div className="field">
            <label>Объект (obj)</label>
            <input value={form.obj} onChange={(e) => setForm({ ...form, obj: e.target.value })} />
          </div>
          <div className="field shrink">
            <button className="primary" type="submit">Выпустить ссылку</button>
          </div>
          <div className="field shrink">
            <button type="button" onClick={() => void useTestSource()}>
              Тестовый источник
            </button>
          </div>
        </div>
      </form>

      {error && <p className="error-text">{error}</p>}

      {result && (
        <>
          <h3>Результат</h3>
          {!result.policy.ok && (
            <p className="error-text">Сетевая политика отклонит этот источник: {result.policy.reason}</p>
          )}
          <p className="muted" style={{ fontSize: 12 }}>
            Ключ потока <code>{result.streamKey}</code>.{' '}
            {result.existingStream
              ? `Такой рестрим уже есть (${result.existingStream.state}, режим ${result.existingStream.mode ?? '?'}) — новая ссылка попадёт в него, второго соединения к регистратору не будет.`
              : 'Рестрима с таким ключом ещё нет — первое обращение его создаст.'}
          </p>
          <CopyRow label="Поток" value={result.live} />
          <CopyRow label="Снапшот" value={result.snapshot} />
          <CopyRow label="Проверка из терминала" value={result.commands.ffplay} />

          {preview ? (
            <Player src={result.live} />
          ) : (
            <button onClick={() => setPreview(true)}>Открыть в плеере</button>
          )}
        </>
      )}
    </div>
  );
}

function EnvelopeInspector() {
  const [url, setUrl] = useState('');
  const [result, setResult] = useState<InspectResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      setResult(await api.inspect({ url }));
    } catch (err) {
      const body = err as Error & { code?: string };
      setResult(null);
      setError(`${body.code ?? 'ОШИБКА'}: ${body.message}`);
    }
  };

  return (
    <div className="panel">
      <h2>Инспектор конверта</h2>
      <p className="muted" style={{ fontSize: 12 }}>
        Вставьте ссылку из приложения, тикета поддержки или лога — покажет, что внутри и где сломалось.
      </p>
      <form onSubmit={submit}>
        <div className="field">
          <label>Ссылка</label>
          <textarea
            required
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://rs.example.ru/s/mj-ios-01/AQAB…/index.m3u8"
          />
        </div>
        <button className="primary" type="submit">Разобрать</button>
      </form>

      {error && <p className="error-text" style={{ marginTop: 12 }}>{error}</p>}

      {result?.ok && result.payload && (
        <>
          <h3>Содержимое</h3>
          <table>
            <tbody>
              <tr><td className="muted">Токен</td><td>{result.token.name} <span className="mono muted">{result.token.id}</span> · {result.token.status}</td></tr>
              <tr><td className="muted">Источник</td><td className="wrap mono">{result.payload.src}</td></tr>
              <tr><td className="muted">Профиль</td><td>{result.payload.profile}</td></tr>
              <tr><td className="muted">Камера / объект</td><td>{result.payload.cam ?? '—'} / {result.payload.obj ?? '—'}</td></tr>
              <tr><td className="muted">Выпущена</td><td>{new Date(result.payload.issuedAt).toLocaleString('ru-RU')}</td></tr>
              <tr><td className="muted">Ключ потока</td><td className="mono">{result.streamKey}</td></tr>
              <tr>
                <td className="muted">Поток сейчас</td>
                <td>
                  {result.stream ? (
                    <>
                      <span className={`badge ${result.stream.state}`}>{result.stream.state}</span>{' '}
                      {result.stream.mode ?? ''}
                      {result.stream.lastError && <div className="error-text mono">{result.stream.lastError}</div>}
                    </>
                  ) : (
                    <span className="muted">не запущен</span>
                  )}
                </td>
              </tr>
              <tr>
                <td className="muted">Сетевая политика</td>
                <td>{result.policy?.ok ? <span className="ok-text">пропускает</span> : <span className="error-text">{result.policy?.reason}</span>}</td>
              </tr>
            </tbody>
          </table>
          {result.usedPreviousSecret && (
            <p className="warn-text">
              Ссылка открылась предыдущим секретом. После того как Мажордом перевыпустит ссылки, предыдущий секрет
              можно убрать на вкладке «Токены».
            </p>
          )}
        </>
      )}
    </div>
  );
}

export function Tools() {
  return (
    <>
      <div className="topbar">
        <h1>Инструменты</h1>
      </div>
      <LinkGenerator />
      <EnvelopeInspector />
    </>
  );
}
